<script setup lang="ts">
import { computed, getCurrentInstance, ref, watch } from "vue";
import store from "@/state/store";
import { roleOptions } from "@/composables/profileUtils";
import {
  KCMPPFieldType,
  KCMPPValuesTable,
  KCMPPProfile,
} from "@/types/kcm-profile-api.types";

import PhotoUpload from "@/components/profile/photo-upload.vue";
import { extractColorsFromSrc } from "extract-colors";
import { FinalColor } from "extract-colors/lib/types/Color";
import { hexToRgb } from "@/composables/colorMath";
import Multiselect from "vue-multiselect";
import { setPersonalization } from "./useSetup";
import { personalizationOnboardingHeader } from "./usePersonalizationOnboarding";

const emit = defineEmits<{ (e: "exit"): void; (e: "save"): void }>();

/**
 * Profile state
 */
const activeImg = ref("");

const loadedProfile = computed(() =>
  store.getters["profile/getProfileByFeature"]("basic")
);

const photo = ref(loadedProfile.value?.photo?.value ?? "");
const logo = ref(loadedProfile.value?.logo?.value ?? "");
const logoPalette = ref<string[]>([]);
const company = ref(loadedProfile.value?.company?.value ?? "");
const jobTitle = ref(loadedProfile.value?.job_title?.value ?? "");
const brandColor = ref(
  "#" + (loadedProfile.value?.primary_color?.value ?? "#00aeef")
);

const role = ref(
  roleOptions.find((role) => {
    return role.value === loadedProfile.value?.primary_role?.value;
  }) || roleOptions[0]
);

/**
 * Photo/Logo upload logic
 */
function setUploader(type: string): void {
  activeImg.value = type;
}

function imageUploaded(url: string): void {
  const cleanedUrl = url.replace(/^https?:/, "");
  if (activeImg.value === "photo") photo.value = cleanedUrl;
  if (activeImg.value === "logo") logo.value = cleanedUrl;
  activeImg.value = "";
}

watch(logo, async (value: string): Promise<void> => {
  try {
    const palette = await extractColorsFromSrc(`https:${value}`, {
      pixels: 64000,
      distance: 0.33,
      crossOrigin: "anonymous",
    });
    logoPalette.value = palette.map((color: FinalColor) => color.hex);
  } catch (err) {
    console.error("Error extracting logo colors:", err);
  }
});

/**
 * Brand color helpers
 */
const contrastColor = computed(() => {
  const rgb = hexToRgb(brandColor.value);
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness > 170 ? "black" : "white";
});

const badColorClass = computed((): string => {
  return contrastColor.value === "white" ? "d-none" : "text-danger fw-bold";
});

/**
 * Profile update logic
 */
const saving = ref(false);
const saveError = ref(false);
const missingRole = ref(false);

const firstName = computed(() => {
  return (
    loadedProfile.value?.first_name?.value ??
    store.getters["auth/authFirstName"]
  );
});
const lastName = computed(() => {
  return (
    loadedProfile.value?.last_name?.value ?? store.getters["auth/authLastName"]
  );
});

const fullname = computed(() => {
  return firstName.value + " " + lastName.value;
});

const getProfilePayload = computed(
  (): KCMPPProfile => ({
    first_name: {
      value: firstName.value,
      field_type: KCMPPFieldType.STRING,
      SyncTo: "",
      fontSize: 0,
      table: KCMPPValuesTable.EMPTY,
    },
    last_name: {
      value: lastName.value,
      field_type: KCMPPFieldType.STRING,
      SyncTo: "",
      fontSize: 0,
      table: KCMPPValuesTable.EMPTY,
    },
    full_name: {
      value: loadedProfile.value?.full_name?.value ?? fullname.value,
      field_type: KCMPPFieldType.STRING,
      SyncTo: "",
      fontSize: 0,
      table: KCMPPValuesTable.EMPTY,
    },
    photo: {
      value: photo.value,
      fontSize: 0,
      field_type: KCMPPFieldType.STRING,
      table: KCMPPValuesTable.EMPTY,
      SyncTo: "",
    },
    logo: {
      value: logo.value,
      fontSize: 0,
      field_type: KCMPPFieldType.STRING,
      table: KCMPPValuesTable.EMPTY,
      SyncTo: "",
    },
    primary_color: {
      value: brandColor.value,
      fontSize: 0,
      field_type: KCMPPFieldType.STRING,
      table: KCMPPValuesTable.EMPTY,
      SyncTo: "",
    },
    company: {
      value: company.value,
      fontSize: 0,
      field_type: KCMPPFieldType.STRING,
      table: KCMPPValuesTable.EMPTY,
      SyncTo: "",
    },
    job_title: {
      value: jobTitle.value,
      fontSize: 0,
      field_type: KCMPPFieldType.STRING,
      table: KCMPPValuesTable.EMPTY,
      SyncTo: "",
    },
    primary_role: {
      value: role.value?.value ?? "",
      fontSize: 0,
      field_type: KCMPPFieldType.STRING,
      table: KCMPPValuesTable.EMPTY,
      SyncTo: "",
    },
    city: {
      value: loadedProfile.value?.city?.value ?? "",
      field_type: KCMPPFieldType.STRING,
      SyncTo: "",
      fontSize: 0,
      table: KCMPPValuesTable.EMPTY,
    },
    state: {
      value: loadedProfile.value?.state?.value ?? "",
      field_type: KCMPPFieldType.STRING,
      SyncTo: "",
      fontSize: 0,
      table: KCMPPValuesTable.EMPTY,
    },
    location: {
      value: loadedProfile.value?.location?.value ?? "",
      field_type: KCMPPFieldType.STRING,
      SyncTo: "",
      fontSize: 0,
      table: KCMPPValuesTable.EMPTY,
    },
  })
);

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const closeModal = () => {
  proxy?.$bvModal.hide("onboarding-profile-modal");
};

async function saveProfile(): Promise<void> {
  if (!role.value) {
    missingRole.value = true;
    return;
  }

  saving.value = true;
  saveError.value = false;

  const resp = await setPersonalization(getProfilePayload.value);

  if (!resp) {
    saving.value = false;
    saveError.value = true;
    return;
  }

  saving.value = false;

  proxy!.$root.$emit("save");
  closeModal();
}
</script>

<template>
  <b-modal
    id="onboarding-profile-modal"
    size="lg"
    centered
    hide-footer
    hide-header
    scrollable
    content-class="p-0"
  >
    <div class="personalization-onboarding-modal py-3">
      <div class="container d-flex flex-column align-items-center">
        <h2 class="profile-header text-primary-800 fw-bold">
          Your Brand Everywhere
        </h2>
        <p class="mb-3">
          {{ personalizationOnboardingHeader }}
        </p>

        <!-- Upload Cards -->
        <div class="d-flex flex-wrap justify-content-center gap-4 mb-3">
          <!-- Photo -->
          <b-card
            @click="setUploader('photo')"
            v-b-modal.modal-image-upload
            class="border border-3 rounded shadow-lg text-center mb-2"
            style="width: 220px; height: 140px; cursor: pointer"
            header-class="bg-white p-3 d-flex justify-content-center align-items-center"
            body-class="bg-light p-2 d-flex justify-content-center align-items-center"
          >
            <template #header>
              <div
                style="height: 40px"
                class="d-flex justify-content-center align-items-center"
              >
                <img v-if="photo" :src="photo" class="setup-profile-image" />
                <iconify-icon
                  v-else
                  icon="fa6-regular:user"
                  class="text-primary-800 xxl"
                />
              </div>
            </template>
            <div>
              <span v-if="photo">Change</span><span v-else>Add</span> your photo
            </div>
          </b-card>

          <!-- Logo -->
          <b-card
            @click="setUploader('logo')"
            v-b-modal.modal-image-upload
            class="border border-3 rounded shadow-lg text-center mb-2"
            style="width: 220px; height: 140px; cursor: pointer"
            header-class="bg-white p-3 d-flex justify-content-center align-items-center"
            body-class="bg-light p-2 d-flex justify-content-center align-items-center"
          >
            <template #header>
              <div
                style="height: 40px"
                class="d-flex justify-content-center align-items-center"
              >
                <img v-if="logo" :src="logo" class="setup-profile-image" />
                <iconify-icon
                  v-else
                  icon="fa6-regular:image"
                  class="text-primary-800 xxl"
                />
              </div>
            </template>
            <div>
              <span v-if="logo">Change</span><span v-else>Add</span> your logo
            </div>
          </b-card>
        </div>

        <PhotoUpload
          :copy="activeImg"
          @imageUploaded="imageUploaded"
          :aspectRatio="activeImg === 'photo' ? 1 / 1 : undefined"
          :bucketSuffix="activeImg"
        />

        <!-- Color Picker + Logo Palette -->
        <div class="row w-100 mb-3">
          <div
            class="col-12 d-flex flex-wrap gap-4"
            :class="logoPalette.length ? 'flex-row' : 'flex-column'"
          >
            <div
              :class="logoPalette.length ? 'flex-grow-1' : 'w-100'"
              :style="logoPalette.length ? 'flex-basis: 48%' : ''"
            >
              <label>Pick a custom brand color</label>
              <b-input-group class="mb-2">
                <b-button
                  variant="outline-light"
                  class="w-100"
                  :class="'text-' + contrastColor"
                  :style="{ background: brandColor }"
                >
                  {{ brandColor }}
                  <b-form-input
                    v-model="brandColor"
                    lazy
                    type="color"
                    class="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
                  />
                </b-button>
              </b-input-group>
              <p :class="badColorClass">
                We suggest using a darker color for compliance
              </p>
            </div>

            <div
              v-if="logoPalette.length"
              class="flex-grow-1"
              style="flex-basis: 48%"
            >
              <label>From your logo</label>
              <div class="d-flex flex-wrap gap-2 mt-2">
                <div
                  v-for="(color, i) in logoPalette"
                  class="paletteColor"
                  :class="color === brandColor ? 'active' : ''"
                  :key="`logo-color-${i}`"
                  style="width: 32px; height: 32px; cursor: pointer"
                  :style="{ backgroundColor: color }"
                  @click="brandColor = color"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Profile Inputs -->
        <div class="row mb-4 w-100">
          <div class="col-md-6 mb-3">
            <label class="form-label">Company</label>
            <b-input v-model="company" />
          </div>

          <div class="col-md-6 mb-3">
            <label class="form-label">
              <span class="text-danger">*</span> Primary Role
            </label>
            <multiselect
              v-model="role"
              placeholder="Select your primary role"
              selectLabel=""
              :searchable="false"
              :show-labels="false"
              :allow-empty="false"
              :required="true"
              :options="roleOptions"
              :class="missingRole && !role ? 'invalid' : ''"
            >
              <template #singleLabel="props">
                <div style="font-size: 1rem; line-height: 1.5">
                  <iconify-icon :icon="props.option.icon" class="me-2" inline />
                  <span>{{ props.option.label }}</span>
                </div>
              </template>
              <template #option="props">
                <div style="font-size: 1rem; line-height: 1.5">
                  <iconify-icon :icon="props.option.icon" class="me-2" inline />
                  <span>{{ props.option.label }}</span>
                </div>
              </template>
            </multiselect>
            <div v-if="missingRole && !role" class="invalid-feedback d-block">
              Primary Role is required.
            </div>
          </div>

          <div class="col-12">
            <label class="form-label">Job Title</label>
            <b-input v-model="jobTitle" />
          </div>
        </div>

        <!-- Buttons -->
        <div class="m-2 d-flex flex-column align-items-center gap-3 w-100">
          <b-button
            class="w-75"
            variant="primary"
            :disabled="saving"
            @click="saveProfile"
          >
            {{ saving ? "Saving..." : "Save and Continue" }}
          </b-button>

          <b-alert variant="danger" class="mt-3" :show="saveError">
            Something went wrong while saving your profile. Please try again.
          </b-alert>
          <p>Don't worry, you can always edit this profile later on.</p>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<style scoped>
.paletteColor {
  border-radius: 50%;
  width: 40px;
  height: 40px;
  background-color: var(--backgroundColor);
  display: inline-block;
  border: 3px solid #fff;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.4), inset 0 0 3px rgba(0, 0, 0, 0.3);
}
</style>
